import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { SignUpForm } from '@tempots/beatui/auth'
import { attr, Fragment, html, Use, on } from '@tempots/dom'
import { Anchor, HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { RequireAnon } from '@/components/require-anon'
import { EphimeralService } from '@/utils/ephimeral'
import { OnboardingInfo } from '@/models/onboarding-info'
import { getPasswordErrorMessage } from '@rh/shared'
import { Track } from '@/services/track'

function validateName(name: string) {
  if (!name) {
    return 'Name is required'
  }
  if (name.length < 3) {
    return 'Name must be at least 3 characters'
  }
  return null
}

function validateEmail(email: string) {
  const parts = email.split('@')
  if (parts.length !== 2) {
    return 'Invalid email format'
  }
  if (!parts[0] || !parts[1]) {
    return 'Invalid email format'
  }
  if (parts[1].length < 3) {
    return 'Invalid email format'
  }
  if (!parts[1].includes('.')) {
    return 'Invalid email format'
  }
  return null
}

function validatePassword(password: string) {
  return getPasswordErrorMessage(password)
}

export function SignupPage() {
  return RequireAnon({}, () =>
    OnboardingLayout(
      {},
      HTMLTitle('Sign Up - ResumeHeaders'),
      html.div(
        attr.class('w-full md:max-w-md'),
        html.div(
          attr.class('flex justify-center mb-6 md:mb-8'),
          Anchor({ href: '/' }, Logo({ size: 'main' }))
        ),
        html.h2(
          attr.class('text-lg md:text-xl font-bold text-center mb-3 md:mb-4'),
          'Welcome to ResumeHeaders.com!'
        ),
        html.p(
          attr.class(
            'text-center mb-6 md:mb-8 text-sm md:text-base text-gray-600'
          ),
          'Start your free 14-day trial — no credit card required.'
        ),
        Use(Location, location => {
          // A pricing-card CTA carries the chosen plan; stash it so the
          // post-onboarding redirect can land on billing with that intent.
          const planIntent = new URLSearchParams(window.location.search).get(
            'plan'
          )
          if (planIntent && ['standard', 'pro', 'team'].includes(planIntent)) {
            EphimeralService.store({ type: 'planIntent', plan: planIntent })
          }
          let {
            headerRecruiterEmail: recruiterEmail,
            headerRecruiterName: recruiterName,
          } = EphimeralService.retrieve<OnboardingInfo>('onboarding') ?? {
            type: 'onboarding',
          }
          if (recruiterEmail?.endsWith('@example.com')) {
            recruiterEmail = undefined
          }
          if (recruiterName === 'Your Name') {
            recruiterName = undefined
          }
          return Fragment(
            SignUpForm({
              showAlreadyHaveAccountLink: true,
              showConfirmPassword: false,
              initialEmail: recruiterEmail ?? undefined,
              initialName: recruiterName ?? undefined,
              showAcceptTermsAndConditions: true,
              showNameField: true,
              termsAndConditions: html.span(
                attr.class('text-sm text-gray-600'),
                'By signing up, you agree to our ',
                html.a(
                  attr.href('/terms'),
                  attr.class('text-teal-600 hover:underline'),
                  'Terms of Service'
                ),
                ' and ',
                html.a(
                  attr.href('/privacy'),
                  attr.class('text-teal-600 hover:underline'),
                  'Privacy Policy'
                )
              ),
              onSignUp: async data => {
                Track.click({
                  section: 'main',
                  element_type: 'button',
                  click_action: 'submit',
                  element_id: 'signup',
                })
                data.name = data.name?.trim() ?? ''
                data.email = data.email?.trim() ?? ''
                data.password = data.password?.trim() ?? ''
                const nameErrors = validateName(data.name)
                if (nameErrors != null) {
                  Track.clientError({
                    error_code: 'SIGNUP_INVALID_NAME',
                    error_message: 'Invalid name',
                  })
                  return nameErrors
                }
                const emailErrors = validateEmail(data.email)
                if (emailErrors != null) {
                  Track.clientError({
                    error_code: 'SIGNUP_INVALID_EMAIL',
                    error_message: 'Invalid email',
                  })
                  return emailErrors
                }
                const passwordErrors = validatePassword(data.password)
                if (passwordErrors != null) {
                  Track.clientError({
                    error_code: 'SIGNUP_INVALID_PASSWORD',
                    error_message: 'Invalid password',
                  })
                  return passwordErrors
                }
                if (!data.acceptTerms) {
                  Track.clientError({
                    error_code: 'SIGNUP_ACCEPT_TERMS',
                    error_message: 'Please accept the terms and conditions',
                  })
                  return 'Please accept the terms and conditions.'
                }
                try {
                  const { user, message } = await Auth.signup({
                    email: data.email,
                    password: data.password,
                    fullName: data.name ?? data.email,
                  })
                  Track.auth({
                    action: 'signup',
                    user_id: user?.id,
                    message,
                  })
                  location.navigate(
                    `/verify-email?email=${encodeURIComponent(data.email)}`
                  )
                  return null
                } catch (error) {
                  Track.apiError({
                    error_code: 'SIGNUP_FAILED',
                    error_message:
                      error instanceof ApiError
                        ? error.message
                        : 'Unknown error',
                  })
                  if (error instanceof ApiError) {
                    return (
                      error.message ??
                      'Unable to complete sign up. Please try again.'
                    )
                  }

                  return 'An unexpected error occurred. Please try again.'
                }
              },
            }),
            html.div(
              attr.class(
                'mt-8 flex flex-col gap-2 text-sm text-center text-gray-600'
              ),
              html.span(
                'Already have an account? ',
                html.button(
                  attr.type('button'),
                  attr.class('text-teal-600 hover:underline font-medium'),
                  on.click(() => {
                    Track.click({
                      section: 'main',
                      element_type: 'button',
                      click_action: 'navigate',
                      element_id: 'signin',
                    })
                    location.navigate('/signin')
                  }),
                  'Sign in'
                )
              ),
              html.span(
                'Need to reset your password? ',
                html.button(
                  attr.type('button'),
                  attr.class('text-teal-600 hover:underline font-medium'),
                  on.click(() => {
                    Track.click({
                      section: 'main',
                      element_type: 'button',
                      click_action: 'navigate',
                      element_id: 'forgot_password',
                    })
                    location.navigate('/forgot-password')
                  }),
                  'Request a reset link'
                )
              )
            )
          )
        })
      )
    )
  )
}
